import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaBoxOpen, FaPlus, FaArrowLeft } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import productService from '../utils/productService';

const ProductStockAdd = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [amount, setAmount] = useState(0);

  useEffect(() => {
    productService.getById(id).then(setProduct).catch(() => setProduct(null));
  }, [id]);

  const currentStock = Number(product?.stock) || 0;
  const minimumStock = Number(product?.minimum_stock) || 0;
  const newStock = useMemo(() => currentStock + (Number(amount) || 0), [currentStock, amount]);

  if (!product) {
    return (
      <section className="col-span-1 md:col-span-10 p-4 md:py-6">
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-left shadow-sm">
          <h2 className="text-xl font-semibold text-slate-900">{t('dashboard.products.detailNotFound')}</h2>
          <button
            onClick={() => navigate('/dashboard/products')}
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-slate-100 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-200"
          >
            <FaArrowLeft className="h-4 w-4" /> {t('dashboard.products.backToProducts')}
          </button>
        </div>
      </section>
    );
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    await productService.addStock(id, amount);
    navigate('/dashboard/products');
  };

  return (
    <section className="col-span-1 md:col-span-10 p-4 md:py-6">
      <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-4 shadow-sm md:p-6">
        <div className="mb-6 flex flex-col justify-between gap-4 rounded-lg bg-white p-5 text-left shadow-sm md:flex-row md:items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700">
              {t('dashboard.products.addStock')}
            </p>
            <h2 className="mt-2 text-3xl font-semibold text-[#00595A]">
              {product.name}
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              {t('dashboard.products.addStockSubtitle')}
            </p>
          </div>
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded bg-[#00595A] text-white shadow-sm">
            <FaBoxOpen className="h-6 w-6" />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2 md:p-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="text-sm font-medium text-slate-700">{t('dashboard.products.stockToAdd')}</label>
                <input
                  type="number"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  className="mt-1 block w-full rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-[#00595A] px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#004446]"
                >
                  <FaPlus className="h-4 w-4" /> {t('dashboard.products.addStock')}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/dashboard/products')}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-slate-100 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-200"
                >
                  <FaArrowLeft className="h-4 w-4" /> {t('dashboard.common.back')}
                </button>
              </div>
            </form>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-5 text-left shadow-sm md:p-6">
            <h3 className="text-lg font-semibold text-slate-900">{t('dashboard.products.stockInfo')}</h3>
            <div className="mt-4 space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-slate-600">{t('dashboard.products.currentStock')}</span>
                <span className="font-semibold text-slate-900">{currentStock}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">{t('dashboard.products.minimumStock')}</span>
                <span className="font-semibold text-slate-900">{minimumStock}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">{t('dashboard.products.afterAdd')}</span>
                <span className="font-semibold text-slate-900">{newStock}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductStockAdd;
